import { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable, Platform, Animated } from 'react-native';
import { User, LogOut, MessageSquare, ChevronDown } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { useRef } from 'react';

interface ChatHeaderProps {
  onShowConversations: () => void;
  onSignOut: () => void;
}

const VIOLET       = '#7C3AED';
const VIOLET_DIM   = 'rgba(124, 58, 237, 0.15)';
const VIOLET_GLOW  = 'rgba(124, 58, 237, 0.25)';
const SURFACE      = '#1E1B3A';
const BORDER       = '#2D2B50';
const TEXT         = '#FFFFFF';
const TEXT_MUTED   = '#A0A0B8';

export function ChatHeader({ onShowConversations, onSignOut }: ChatHeaderProps) {
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loading, setLoading]     = useState(true);
  const [showMenu, setShowMenu]   = useState(false);
  const menuAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => { loadUser(); }, []);

  useEffect(() => {
    Animated.spring(menuAnim, {
      toValue: showMenu ? 1 : 0,
      tension: 120, friction: 12,
      useNativeDriver: true,
    }).start();
  }, [showMenu]);

  const loadUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) setUserEmail(user.email);
    } catch (e) {
      console.error('[ChatHeader]', e);
    } finally {
      setLoading(false);
    }
  };

  // Short display: first part before @
  const displayName = userEmail ? userEmail.split('@')[0] : '';

  if (loading || !userEmail) return <View style={styles.placeholder} />;

  return (
    <View style={styles.container}>
      {/* Avatar button */}
      <Pressable
        style={({ pressed }) => [
          styles.avatarBtn,
          pressed  && styles.avatarBtnPressed,
          showMenu && styles.avatarBtnActive,
        ]}
        onPress={() => setShowMenu(v => !v)}
        accessibilityLabel="תפריט משתמש"
        accessibilityRole="button"
      >
        {/* Violet ring when active */}
        {showMenu && <View style={styles.activeRing} />}

        {/* Avatar circle with initial */}
        <View style={styles.avatarCircle}>
          <Text style={styles.avatarInitial}>
            {displayName.charAt(0).toUpperCase()}
          </Text>
        </View>

        <ChevronDown
          size={14}
          color={showMenu ? VIOLET : TEXT_MUTED}
          style={{ transform: [{ rotate: showMenu ? '180deg' : '0deg' }] }}
        />
      </Pressable>

      {/* Dropdown */}
      {showMenu && (
        <>
          {Platform.OS === 'web' && (
            <Pressable style={styles.overlay} onPress={() => setShowMenu(false)} />
          )}
          <Animated.View
            style={[
              styles.menu,
              {
                opacity:   menuAnim,
                transform: [{ translateY: menuAnim.interpolate({ inputRange: [0,1], outputRange: [-8,0] }) }],
              },
            ]}
          >
            {/* Email label */}
            <View style={styles.emailRow}>
              <Text style={styles.emailText} numberOfLines={1}>{userEmail}</Text>
            </View>

            <View style={styles.divider} />

            {/* Conversations */}
            <Pressable
              style={({ pressed }) => [styles.menuItem, pressed && styles.menuItemPressed]}
              onPress={() => { setShowMenu(false); onShowConversations(); }}
              accessibilityLabel="השיחות שלי"
              accessibilityRole="button"
            >
              <MessageSquare size={17} color={TEXT_MUTED} />
              <Text style={styles.menuItemText}>השיחות שלי</Text>
            </Pressable>

            <View style={styles.divider} />

            {/* Sign out */}
            <Pressable
              style={({ pressed }) => [styles.menuItem, pressed && styles.menuItemPressed]}
              onPress={() => { setShowMenu(false); onSignOut(); }}
              accessibilityLabel="התנתקות"
              accessibilityRole="button"
            >
              <LogOut size={17} color={theme.colors.error} />
              <Text style={[styles.menuItemText, { color: theme.colors.error }]}>התנתקות</Text>
            </Pressable>
          </Animated.View>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  placeholder: { width: 56, height: 44 },

  container: { position: 'relative' },

  // ── Avatar button ──────────────────────────────────────────────────────────
  avatarBtn: {
    flexDirection:  'row',
    alignItems:     'center',
    gap:            6,
    paddingHorizontal: 8,
    paddingVertical:   6,
    borderRadius:   24,
    borderWidth:    1,
    borderColor:    BORDER,
    backgroundColor: SURFACE,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.20)' } as any
      : { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.18, shadowRadius: 6, elevation: 4 }),
  },
  avatarBtnPressed: { opacity: 0.80 },
  avatarBtnActive:  { borderColor: VIOLET, backgroundColor: VIOLET_DIM },

  activeRing: {
    position:     'absolute',
    top: -2, left: -2, right: -2, bottom: -2,
    borderRadius: 26,
    borderWidth:  1.5,
    borderColor:  VIOLET,
    opacity:      0.50,
  },

  avatarCircle: {
    width:           32,
    height:          32,
    borderRadius:    16,
    backgroundColor: VIOLET,
    justifyContent:  'center',
    alignItems:      'center',
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 2px 8px ${VIOLET_GLOW}` } as any
      : { shadowColor: VIOLET, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.35, shadowRadius: 6, elevation: 4 }),
  },
  avatarInitial: {
    color:      TEXT,
    fontSize:   14,
    fontFamily: theme.fonts.bold,
    lineHeight: 18,
  },

  // ── Dropdown menu ──────────────────────────────────────────────────────────
  overlay: { position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 },

  menu: {
    position:        'absolute',
    top:             52,
    left:            0,
    minWidth:        180,
    backgroundColor: SURFACE,
    borderRadius:    14,
    borderWidth:     1,
    borderColor:     BORDER,
    zIndex:          1000,
    overflow:        'hidden',
    ...(Platform.OS === 'web'
      ? { boxShadow: `0 8px 24px rgba(0,0,0,0.35), 0 0 0 1px ${BORDER}` } as any
      : { shadowColor: '#000', shadowOffset: { width: 0, height: 6 }, shadowOpacity: 0.30, shadowRadius: 16, elevation: 8 }),
  },

  emailRow: {
    paddingHorizontal: 14,
    paddingVertical:   10,
  },
  emailText: {
    fontSize:   11,
    fontFamily: theme.fonts.regular,
    color:      TEXT_MUTED,
    textAlign:  'right',
    writingDirection: 'rtl' as any,
  },

  divider: { height: 1, backgroundColor: BORDER },

  menuItem: {
    flexDirection:     'row-reverse',
    alignItems:        'center',
    gap:               10,
    paddingVertical:   12,
    paddingHorizontal: 14,
  },
  menuItemPressed: { backgroundColor: 'rgba(255,255,255,0.04)' },
  menuItemText: {
    fontSize:   14,
    fontFamily: theme.fonts.medium,
    color:      TEXT,
    flex:       1,
    textAlign:  'right',
  },
});